import type {
  ConsumeHandoffTokenOptions,
  HandoffFailureReason,
  HandoffResult,
} from './types';

const DEFAULT_TIMEOUT_MS = 5_000;

function mapStatusToReason(status: number): HandoffFailureReason {
  switch (status) {
    case 410: return 'expired';
    case 409: return 'consumed';
    case 403: return 'wrong_target';
    default:  return 'unknown';
  }
}

/**
 * Server-side handoff token redemption.
 *
 * Calls the destination's own PHP backend at `POST /api/v1/inbound/handoff/exchange`
 * (controller mounted by `auth-service-helper` in phase D2c). The backend relays
 * to auth-service `POST /handoff-tokens/{token}/exchange` with its X-API-KEY,
 * then returns the resolved user + session token to this Next layer.
 *
 * Never call auth-service directly from here — keep service API keys in PHP.
 */
export async function consumeHandoffToken(
  options: ConsumeHandoffTokenOptions,
): Promise<HandoffResult> {
  const { token, backendUrl, serviceApiKey, internalToken } = options;
  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let response: Response;
  try {
    response = await fetch(`${backendUrl}/api/v1/inbound/handoff/exchange`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-API-KEY': serviceApiKey,
        'X-Internal-Token': internalToken,
      },
      body: JSON.stringify({ token }),
      signal: controller.signal,
      cache: 'no-store',
    });
  } catch {
    clearTimeout(timer);
    return { ok: false, reason: 'network' };
  }
  clearTimeout(timer);

  if (!response.ok) {
    return { ok: false, reason: mapStatusToReason(response.status), status: response.status };
  }

  let body: unknown;
  try {
    body = await response.json();
  } catch {
    return { ok: false, reason: 'unknown', status: response.status };
  }

  if (!isExchangeBody(body)) {
    return { ok: false, reason: 'unknown', status: response.status };
  }

  return {
    ok: true,
    user: {
      uuid: body.user.uuid,
      name: body.user.name,
      email: body.user.email,
    },
    sessionToken: body.session_token,
    shareId: body.share_id,
    nextPath: body.next_path ?? '/',
  };
}

function isExchangeBody(value: unknown): value is {
  user: { uuid: string; name: string; email: string };
  session_token: string;
  share_id: string;
  next_path?: string | null;
} {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (typeof v.session_token !== 'string') return false;
  if (typeof v.share_id !== 'string') return false;
  const u = v.user as Record<string, unknown> | undefined;
  if (!u || typeof u !== 'object') return false;
  return (
    typeof u.uuid === 'string' &&
    typeof u.name === 'string' &&
    typeof u.email === 'string'
  );
}
